/**
 * Central site content. Per D-013 there is no CMS - public content lives here in
 * code and a developer edits it. Keep copy/marketing text in this folder so
 * non-engineers can find it easily and changes are reviewable in Git.
 *
 * NOTE: copy/photos are PLACEHOLDER until BB provides real content
 * (docs/PLANNING.md open question #7).
 */
import { Eye, ShieldCheck, Truck, Headset, type LucideIcon } from "lucide-react";

export const company = {
  name: "Bull and Bear Co",
  shortName: "Bull & Bear",
  tagline: "Tanker freight, hauled right and tracked every mile.",
  description:
    "Bull and Bear Co is a tanker trucking company hauling liquid bulk freight across all 48 states and into Canada - modern equipment, professional drivers, live ELD tracking, and dispatch that answers the phone.",
  address: {
    line1: "75 Executive Dr #307",
    city: "Aurora",
    state: "IL",
    zip: "60504",
  },
  phone: "+1 (513) 277-0330",
  // PLACEHOLDER - confirm the real inbox (docs/PLANNING.md open question #8).
  email: "info@bullandbearco.com",
  social: {
    linkedin: "https://www.linkedin.com/company/bull-and-bear-co/",
    facebook: "https://www.facebook.com/bullbearco/",
    instagram: "https://www.instagram.com/bullbearco/",
  },
} as const;

/** Single-line address string for compact display (hero CTA band, etc.). */
export const addressText = `${company.address.line1}, ${company.address.city}, ${company.address.state} ${company.address.zip}`;

/** Year BB started operating. Drives the "years moving freight" stat below. */
export const FOUNDED_YEAR = 2015;

/** Headline stats (PLACEHOLDER figures - confirm real numbers). */
export const stats = {
  /** Derived from FOUNDED_YEAR so it never goes stale. */
  years: `${new Date().getFullYear() - FOUNDED_YEAR}+`,
  lanes: "48",
  onTime: "98.6%",
} as const;

export interface ValueProp {
  title: string;
  body: string;
  icon: LucideIcon;
}

/** Home "why us" value props (PLACEHOLDER copy). */
export const valueProps: ValueProp[] = [
  { title: "Live visibility", body: "Every load tracked end-to-end with ELD-fed location and status.", icon: Eye },
  { title: "Safety-first", body: "DOT-compliant fleet, vetted professional drivers, HazMat endorsed.", icon: ShieldCheck },
  { title: "Modern equipment", body: "Late-model tractors and stainless tankers, maintained on schedule.", icon: Truck },
  { title: "People who answer", body: "A dispatch team that picks up the phone - day or night.", icon: Headset },
];

export const mainNav = [
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "Track", to: "/track" },
  { label: "Careers", to: "/careers" },
  { label: "About", to: "/about" },
  { label: "People", to: "/people" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
] as const;

export const footerNav = [
  {
    heading: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "People & shop", to: "/people" },
      { label: "Services", to: "/services" },
      { label: "Industries", to: "/industries" },
      { label: "Contact", to: "/contact" },
      { label: "Blog", to: "/blog" },
    ],
  },
  {
    heading: "Work with us",
    links: [
      { label: "Careers", to: "/careers" },
      { label: "Drive for us", to: "/careers#openings" },
      { label: "Owner-operators", to: "/apply?type=carrier" },
      { label: "Track a shipment", to: "/track" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Privacy", to: "/privacy" },
      { label: "Terms", to: "/terms" },
    ],
  },
] as const;
